import { Box, Dialog, DialogActions, DialogContent, Tooltip } from '@mui/material';
import { BodyText, CaptionText, Subheading } from '../../shared/typography/PrimaryTypography';
import GhostButton from '../../shared/button/GhostButton';
import { ImportDataGridRow } from './ImportDataGrid';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { ImportErrorGroups, ImportErrorRows } from '@esg/esg-global-types';
import { formatTitleCase } from '../../../util/general_utils';

interface ImportErrorsModalProps {
  open: boolean;
  import_row: ImportDataGridRow | null;
  handleCloseErrorsModal: () => void;
}

interface ImportErrorGridRow {
  id: string;
  error_group: string;
  row: number;
  error: Array<string>;
}

/**
 * Modal to display a detailed breakdown of entity level errors that occurred during an import, causing it to fail.
 * @param {boolean} open Control the display state of the modal.
 * @param {ImportDataGridRow | null} import_row Failed import row to view.
 * @param {(): void} handleCloseErrorsModal Close the modal.
 * @returns {JSX.Element}
 */
const ImportErrorsModal = ({
  open,
  import_row,
  handleCloseErrorsModal
}: ImportErrorsModalProps) => {
  if (!import_row) return null;

  const import_errors: ImportErrorRows | undefined =
    !!import_row && 'errors' in import_row.metadata ? import_row.metadata.errors : undefined;
  const error_rows: Array<ImportErrorGridRow> = [];

  // Transform failed import errors object to datagrid rows and check that the errors object is of the correct structure.
  if (import_errors && typeof import_errors === 'object') {
    Object.keys(import_errors).forEach((error_index: string) => {
      if (!isNaN(Number(error_index))) {
        const index_error_groups: ImportErrorGroups = import_errors[Number(error_index)];
        Object.keys(index_error_groups).forEach((error_group: string) => {
          error_rows.push({
            id: `${error_index}-${error_group}`,
            error_group: error_group,
            row: Number(error_index),
            error: index_error_groups[error_group]
          });
        });
      }
    });
  }

  const start_time: string = import_row.metadata.started;

  // Only show row column when not all errors are logged under 0 index (CSV / user row input import).
  const show_row_column: boolean = error_rows.some(
    (error_row: ImportErrorGridRow) => error_row.row !== 0
  );

  const columns: Array<GridColDef> = [
    {
      field: 'error_group',
      headerName: 'Error Type',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <span>{formatTitleCase(params.row.error_group)}</span>
          </Box>
        );
      },
      flex: 0.3
    },
    {
      field: 'row',
      headerName: 'Row',
      flex: 0.1
    },
    {
      field: 'error',
      headerName: 'Error Message',
      renderCell: (params: GridRenderCellParams) => (
        <Tooltip title={params.value}>
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {params.value}
          </span>
        </Tooltip>
      ),
      flex: 1
    }
  ];

  return (
    <Dialog open={open} onClose={handleCloseErrorsModal} fullWidth maxWidth="lg">
      <Subheading weight="Bold" sx={{ m: 2 }}>
        {import_row?.message} - {start_time}
      </Subheading>
      <DialogContent>
        <br />
        <CaptionText weight="Medium">
          Import Tracking ID: {import_row?.metadata.tracking_id}
        </CaptionText>
        <br />
        {import_row && 'errors' in import_row.metadata && error_rows.length > 0 ? (
          <Box>
            <BodyText id="errors-explainer" textAlign={'justify'} sx={{ my: 2 }}>
              While attempting to run the import, the following issues were found with the source
              data items:
            </BodyText>
            <DataGrid
              autoHeight
              rows={error_rows}
              columns={columns}
              disableColumnMenu
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 5
                  }
                },
                sorting: {
                  sortModel: [{ field: 'row', sort: 'asc' }]
                },
                columns: {
                  columnVisibilityModel: {
                    row: show_row_column
                  }
                }
              }}
              pageSizeOptions={[5]}
              disableRowSelectionOnClick
              sx={{
                '&, [class^=MuiDataGrid]': { border: 'none' }
              }}
            />
          </Box>
        ) : (
          <BodyText id="errors-explainer" textAlign={'justify'} sx={{ my: 2 }}>
            Error details not found for import, please{' '}
            <a href="/contact" target="_/blank">
              Contact Us
            </a>{' '}
            for assistance.
          </BodyText>
        )}
        <DialogActions sx={{ mt: 2 }}>
          <GhostButton dark onClick={handleCloseErrorsModal}>
            Back
          </GhostButton>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};

export default ImportErrorsModal;
